/**
 * Central GraphQL error reporting to GlitchTip (Sentry SDK).
 *
 * Every failed operation becomes a resolvable issue grouped by
 * (hasura error code + operation name), tagged with the context needed to act
 * on it: which operation, which Hasura `extensions.code` (permission-error /
 * validation-failed / invalid-jwt / constraint-violation / ...), the error
 * paths, and the variable KEYS. Variable VALUES are never sent — they can
 * carry guest PII.
 *
 * `handled: true` marks errors the app already reacted to (e.g. the
 * invalid-jwt sign-out redirect) so they stay visible without reading as
 * unhandled crashes.
 */

import * as Sentry from '@sentry/nextjs'
import { getLastRequestRole } from '@/lib/active-role'

export interface CapturedGraphQLError {
  message: string
  path?: readonly (string | number)[]
  extensions?: Record<string, unknown>
}

export type GraphQLErrorSource = 'apollo' | 'apollo-ws' | 'gql-request'

export function captureGraphQLErrors(input: {
  operationName?: string | null
  operationType?: string
  errors: readonly CapturedGraphQLError[]
  source: GraphQLErrorSource
  handled?: boolean
  variableKeys?: readonly string[]
}): void {
  const primary = input.errors[0]
  const rawCode = primary?.extensions?.['code']
  const code = typeof rawCode === 'string' && rawCode ? rawCode : 'unknown'
  const operation = input.operationName || 'unknown'

  Sentry.withScope((scope) => {
    scope.setTags({
      'graphql.source': input.source,
      'graphql.operation': operation,
      'graphql.operation_type': input.operationType ?? 'unknown',
      'hasura.code': code,
      'graphql.handled': String(input.handled ?? false),
      // THE role Hasura evaluated permissions against. `auth.role` (set by
      // setMonitoringIdentity) is the JWT DEFAULT role — a different thing,
      // and a red herring on exactly the issues you need it for.
      //
      // ⛔ This must be the role that was SENT, not the per-property role_key.
      // The first version of this tag reported `getPropertyRoleKey()`, but the
      // `x-hasura-role` header is suppressed when that role is absent from
      // `x-hasura-allowed-roles` or equals the default — so the two diverge.
      // Worse, an event from a browser on an older bundle carries NO tag at
      // all, which is easily misread as "ran as the default role" rather than
      // "unknown". Both traps fired on 2026-08-04 and misdirected two
      // investigations. `getLastRequestRole()` records what the transport
      // actually decided, and is null only before the first request.
      ...(getLastRequestRole() ? { 'auth.request_role': getLastRequestRole()! } : {}),
    })
    scope.setExtras({
      errors: input.errors.map((e) => ({
        message: e.message,
        path: e.path,
        code: e.extensions?.['code'],
      })),
      variableKeys: input.variableKeys,
    })
    // Group by failure mode + operation, not by message text (messages embed
    // ids/values and would explode into one issue per occurrence).
    scope.setFingerprint(['graphql', code, operation])

    const error = new Error(`[${code}] ${operation}: ${primary?.message ?? 'GraphQL request failed'}`)
    error.name = 'GraphQLOperationError'
    Sentry.captureException(error)
  })
}

/**
 * Attach the signed-in identity to every subsequent event so issues answer
 * "which role / tenant / property hit this". Values are internal ids on a
 * self-hosted GlitchTip — no third party involved.
 */
export function setMonitoringIdentity(identity: {
  userId: string
  role?: string | null
  tenantId?: string | null
  activePropertyId?: string | null
} | null): void {
  if (!identity) {
    Sentry.setUser(null)
    Sentry.setTags({ 'auth.role': undefined, 'auth.tenant_id': undefined, 'auth.active_property_id': undefined })
    return
  }
  Sentry.setUser({ id: identity.userId })
  Sentry.setTags({
    'auth.role': identity.role ?? undefined,
    'auth.tenant_id': identity.tenantId ?? undefined,
    'auth.active_property_id': identity.activePropertyId ?? undefined,
  })
}
